import React, { FC, useContext, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { AlertWithCustomIcon, IDCardIcon } from "..";
import { PlanType } from "../../@types";
import { IntlContext, TranslationKey } from "../../helpers/intlHooks";
import { WarningIcon } from "@cmsgov/ds-medicare-gov";
import { AlertProps } from "@cmsgov/design-system/dist/types/Alert/Alert";
import classNames from "classnames";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import { useFormattedMessageRemoveCustomTags } from "../../helpers/formattedMessageHelpers";
import { sendAnalyticsEvent } from "../../app/contexts/Analytics";
import { useAppContext } from "../../helpers/context-hooks/useAppContext";
import { isMedicareAdvantageType } from "../../helpers/planTypes";
import { getLinkToMedicare } from "../../helpers/urlHelpers";
export const NeedsMedicareABAlert: FC<
  Omit<AlertProps, "variation"> & {
    headingKey: TranslationKey;
    bodyKey: TranslationKey;
    showAsError?: boolean;
  }
> = ({ headingKey, bodyKey, showAsError = false, className, ...props }) => {
  const {
    state: { language, planType },
    dispatch,
  } = useAppContext();
  const intl = useContext(IntlContext);
  const isMAPlan = !!planType && isMedicareAdvantageType(planType);
  const isPDPlan = planType === PlanType.PDP;
  // Must be a string, so use `String(intl.formatMessage)`
  const needsMedicareABHeading = String(
    intl.formatMessage(
      {
        id: headingKey,
      },
      {
        pdp: chunks => (isPDPlan ? chunks : ""),
        ma: chunks => (isMAPlan ? chunks : ""),
      }
    )
  );

  const alertContentText = useFormattedMessageRemoveCustomTags(bodyKey, [
    { tag: "a" },
    { tag: "pdp", handler: chunks => (isPDPlan ? chunks : "") },
    { tag: "ma", handler: chunks => (isMAPlan ? chunks : "") },
    { tag: "strong" },
  ]);

  useEffect(() => {
    sendAnalyticsEvent({
      event_name: "alert_impression",
      heading: needsMedicareABHeading,
      text: alertContentText,
      type: showAsError ? "error" : "informational",
      link_type: "link_other",
    });
  }, [alertContentText, needsMedicareABHeading, showAsError]);

  const whenCanISignUpForMedicareHref = getLinkToMedicare({
    language,
    mgovUrlKey: "whenCanISignUpForMedicare",
  });

  return (
    <AlertWithCustomIcon
      heading={needsMedicareABHeading}
      Icon={showAsError ? WarningIcon : IDCardIcon} // The error version uses a warning icon
      className={classNames("ds-u-margin-bottom--2", className)}
      variation={showAsError ? "error" : undefined}
      {...props}
    >
      <FormattedMessage
        id={bodyKey}
        values={{
          a: chunks => (
            <a
              href={whenCanISignUpForMedicareHref}
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                  settings: {
                    linkText: String(chunks),
                    linkUrl: whenCanISignUpForMedicareHref,
                    text: String(chunks),
                  },
                });
              }}
              target="_blank"
              rel="noreferrer"
            >
              {chunks}
            </a>
          ),
          pdp: chunks => (isPDPlan ? chunks : ""),
          ma: chunks => (isMAPlan ? chunks : ""),
          strong: chunks => <strong>{chunks}</strong>,
        }}
      />
    </AlertWithCustomIcon>
  );
};
